import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contentAPI } from '@/utils/api';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { User } from 'lucide-react';
import { Loader } from '@/components/ui/loader';

gsap.registerPlugin(ScrollTrigger);

export const About = () => {
  const { t, i18n } = useTranslation();
  const [llpDetails, setLlpDetails] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [llpRes, partnersRes] = await Promise.all([
        contentAPI.getLLPDetails(),
        contentAPI.getPartners()
      ]);
      setLlpDetails(llpRes.data);
      setPartners(partnersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching about data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      gsap.from('.about-section', {
        scrollTrigger: {
          trigger: '.about-section',
          start: 'top 70%'
        },
        opacity: 0,
        y: 50,
        duration: 0.8,
        stagger: 0.2
      });

      gsap.from('.partner-card', {
        scrollTrigger: {
          trigger: '.partners-grid',
          start: 'top 70%'
        },
        opacity: 0,
        y: 50,
        duration: 0.8,
        stagger: 0.15
      });
    }
  }, [loading]);

  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="about-hero"
        className="relative py-32 flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.85), rgba(11, 28, 45, 0.85)), url('https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBib2FyZHJvb218ZW58MHx8fHwxNzcyMjY5ODc4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            About Us
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Leading the way in corporate debt recovery and NPA resolution across India
          </p>
        </div>
      </section>

      {/* Company Description */}
      <section data-testid="company-description" className="about-section py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              {llpDetails && getLangContent(llpDetails.company_name)}
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              {llpDetails && getLangContent(llpDetails.description)}
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="vision-mission" className="about-section py-24 md:py-32 bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="glass-card p-10 rounded-sm">
              <h3 className="font-heading text-3xl font-semibold text-secondary mb-6">Our Vision</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                {llpDetails && getLangContent(llpDetails.vision)}
              </p>
            </div>
            <div className="glass-card p-10 rounded-sm">
              <h3 className="font-heading text-3xl font-semibold text-secondary mb-6">Our Mission</h3>
              <p className="text-gray-300 text-base leading-relaxed">
                {llpDetails && getLangContent(llpDetails.mission)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section data-testid="leadership-team" className="about-section py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">
              Leadership Team
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Meet the experienced professionals driving our success
            </p>
          </div>

          <div className="partners-grid grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner) => (
              <div
                key={partner.name}
                data-testid={`partner-${partner.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="partner-card group bg-[#111827] border border-white/5 hover:border-secondary/50 p-8 rounded-sm card-hover text-center"
              >
                <div className="mb-6">
                  {partner.image_url ? (
                    <img
                      src={partner.image_url}
                      alt={partner.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-secondary/20 group-hover:border-secondary transition-colors"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full mx-auto bg-secondary/10 flex items-center justify-center border-4 border-secondary/20 group-hover:border-secondary transition-colors">
                      <User size={48} className="text-secondary" />
                    </div>
                  )}
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-2 group-hover:text-secondary transition-colors">
                  {partner.name}
                </h3>
                <p className="text-secondary text-sm uppercase tracking-widest mb-4">
                  {getLangContent(partner.designation)}
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {getLangContent(partner.bio)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
